import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../components/ui/Footer';
import logoImage from "../assets/logo.svg";

// FAQ Item component for individual questions
interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  toggle: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, toggle }) => {
  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden shadow-sm hover:shadow transition-shadow">
      <button
        className={`w-full px-6 py-4 text-left font-medium flex justify-between items-center transition-colors ${
          isOpen ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-800 hover:bg-gray-50'
        }`}
        onClick={toggle}
      >
        <span>{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 py-5 bg-white text-gray-700 leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Group of FAQ items by category
interface FAQCategoryProps {
  title: string;
  items: {
    question: string;
    answer: React.ReactNode;
  }[];
}

const FAQCategory: React.FC<FAQCategoryProps> = ({ title, items }) => {
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});

  const toggleItem = (question: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [question]: !prev[question],
    }));
  };

  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
      {items.map((item, index) => (
        <FAQItem
          key={index}
          question={item.question}
          answer={item.answer}
          isOpen={!!openItems[item.question]}
          toggle={() => toggleItem(item.question)}
        />
      ))}
    </div>
  );
};

// Main FAQ page component
const FAQ: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  // FAQ data organized by categories
  const faqData: FAQCategoryProps[] = [
    {
      title: "General Questions",
      items: [
        {
          question: "What is Scribedo?",
          answer: (
            <div>
              <p>Scribedo is an AI-powered medical transcription and note-taking platform designed specifically for healthcare professionals. It automatically transforms your patient visits, calls, and recordings into structured clinical notes with accurate medical coding.</p>
              <p className="mt-2">Our platform enables healthcare providers to reduce documentation time and focus more on patient care.</p>
            </div>
          )
        },
        {
          question: "How does Scribedo work?",
          answer: (
            <div>
              <p>Scribedo operates through a streamlined three-step process:</p>
              <ol className="list-decimal ml-5 mt-2 space-y-2">
                <li><strong>Record</strong>: Capture your patient visits or upload existing audio files.</li>
                <li><strong>Process</strong>: Our AI technology transcribes the audio and analyzes the medical content.</li>
                <li><strong>Review</strong>: Evaluate and edit your structured notes, which include SOAP formats, ICD/CPT codes, and patient instructions.</li>
              </ol>
              <p className="mt-2">The platform utilizes specialized medical AI trained on thousands of clinical interactions to ensure accuracy and adherence to medical documentation standards.</p>
            </div>
          )
        },
        {
          question: "What devices and browsers does Scribedo support?",
          answer: (
            <p>Scribedo is compatible with most modern devices including desktops, laptops, tablets, and smartphones. We support all major browsers including Chrome, Firefox, Safari, and Edge. For optimal performance and functionality, we recommend using the latest version of Chrome or Safari.</p>
          )
        },
        {
          question: "Is Scribedo HIPAA compliant?",
          answer: (
            <div>
              <p>Yes, Scribedo is fully HIPAA compliant. We implement comprehensive security measures to protect patient health information:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li>End-to-end encryption for all data transmission</li>
                <li>Secure, HIPAA-compliant cloud storage</li>
                <li>Regular security audits and vulnerability testing</li>
                <li>Role-based access controls</li>
                <li>Detailed audit logs of all system activity</li>
              </ul>
              <p className="mt-2">We provide Business Associate Agreements (BAAs) to all healthcare providers using our platform.</p>
            </div>
          )
        }
      ]
    },
    {
      title: "Features & Functionality",
      items: [
        {
          question: "What types of clinical documentation can Scribedo generate?",
          answer: (
            <div>
              <p>Scribedo can generate various types of clinical documentation, including:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li>SOAP notes (Subjective, Objective, Assessment, Plan)</li>
                <li>Progress notes</li>
                <li>H&P (History and Physical) reports</li>
                <li>Consultation notes</li>
                <li>Follow-up visit notes</li>
                <li>Procedure notes</li>
              </ul>
              <p className="mt-2">All documentation is customizable according to your specialty and workflow preferences.</p>
            </div>
          )
        },
        {
          question: "Does Scribedo suggest medical codes?",
          answer: (
            <p>Yes, Scribedo automatically suggests relevant ICD-10 diagnosis codes and CPT procedure codes based on the clinical content of your notes. These suggestions facilitate accurate billing and documentation. You can review and modify these suggested codes before finalizing your notes.</p>
          )
        },
        {
          question: "Can I make phone calls through Scribedo?",
          answer: (
            <div>
              <p>Yes, Scribedo includes an integrated phone system designed for patient communications. All calls are automatically recorded (with proper consent) and transcribed. The transcriptions are then processed into structured notes using the same methodology as in-person visits.</p>
              <p className="mt-2">This feature is particularly valuable for telehealth consultations, follow-ups, and brief patient check-ins.</p>
            </div>
          )
        },
        {
          question: "Can I customize the templates for my notes?",
          answer: (
            <p>Yes, Scribedo allows for extensive template customization based on your specialty, workflow, and documentation preferences. You can create and save multiple templates for different types of visits or procedures, ensuring consistency and efficiency in your documentation process.</p>
          )
        }
      ]
    },
    {
      title: "Account & Billing",
      items: [
        {
          question: "What pricing plans does Scribedo offer?",
          answer: (
            <div>
              <p>Scribedo offers several pricing tiers to accommodate different needs:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li><strong>Free Trial</strong>: Access to all features for a limited evaluation period</li>
                <li><strong>Basic Plan</strong>: Essential features for individual healthcare providers</li>
                <li><strong>Professional Plan</strong>: Advanced features with higher usage limits</li>
                <li><strong>Enterprise Plan</strong>: Customized solutions for healthcare organizations</li>
              </ul>
              <p className="mt-2">Visit our <a href="/pricing" className="text-blue-600 hover:underline">Pricing Page</a> for detailed information about each plan and current rates.</p>
            </div>
          )
        },
        {
          question: "How do I upgrade or downgrade my subscription?",
          answer: (
            <p>You can modify your subscription at any time through the Account Settings page by selecting the "Subscription" tab. Changes to your subscription will take effect at the beginning of your next billing cycle. For mid-cycle upgrades, we provide prorated billing for the remainder of the current period.</p>
          )
        },
        {
          question: "Can I cancel my subscription? What happens to my data?",
          answer: (
            <div>
              <p>Yes, you can cancel your subscription at any time through the Account Settings page. When you cancel:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li>You maintain access to all features until the end of your current billing period</li>
                <li>Your data is retained for 30 days after your subscription ends</li>
                <li>You can export all your notes before cancellation for your records</li>
              </ul>
              <p className="mt-2">After the 30-day retention period, your data will be permanently deleted in accordance with our data retention policies.</p>
            </div>
          )
        },
        {
          question: "Are there discounts for academic institutions or non-profits?",
          answer: (
            <p>Yes, Scribedo offers preferential pricing for academic institutions, non-profit healthcare organizations, and clinics serving underserved communities. Please contact our sales team at support@scribedo.com to learn more about our discount programs and eligibility requirements.</p>
          )
        }
      ]
    },
    {
      title: "Technical Support",
      items: [
        {
          question: "How do I get help if I encounter issues?",
          answer: (
            <div>
              <p>We offer multiple support channels for your convenience:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li><strong>In-app Chat Support</strong>: Available during business hours directly within the application</li>
                <li><strong>Email Support</strong>: Contact support@scribedo.com for assistance</li>
                <li><strong>Knowledge Base</strong>: Access our comprehensive help articles and tutorials</li>
                <li><strong>Phone Support</strong>: Available for Enterprise customers</li>
              </ul>
              <p className="mt-2">Our support team typically responds within 24 hours for standard inquiries, and within 4 hours for urgent issues.</p>
            </div>
          )
        },
        {
          question: "What should I do if my recording fails to process?",
          answer: (
            <div>
              <p>If your recording fails to process, please try these troubleshooting steps:</p>
              <ol className="list-decimal ml-5 mt-2 space-y-2">
                <li>Verify your internet connection and refresh the page</li>
                <li>Confirm that the audio file format is supported (.mp3, .wav, .m4a)</li>
                <li>Ensure the file isn't corrupted by playing it in another application</li>
                <li>Try uploading a shorter segment to test if length is the issue</li>
                <li>Clear your browser cache and cookies, then try again</li>
              </ol>
              <p className="mt-2">If these steps don't resolve the issue, please contact our support team with details about the recording and any error messages you received.</p>
            </div>
          )
        }
      ]
    },
    {
      title: "Privacy & Security",
      items: [
        {
          question: "How is my data secured?",
          answer: (
            <div>
              <p>Scribedo implements multiple layers of security to safeguard your data:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li>AES-256 encryption for all data at rest</li>
                <li>TLS 1.3 encryption for all data in transit</li>
                <li>Multi-factor authentication</li>
                <li>Regular security audits and penetration testing</li>
                <li>Data backups with strict access controls</li>
                <li>SOC 2 Type II certified infrastructure</li>
              </ul>
              <p className="mt-2">Our security protocols exceed HIPAA requirements and adhere to industry best practices.</p>
            </div>
          )
        },
        {
          question: "Who has access to my patient data?",
          answer: (
            <p>Only you and authorized members of your practice have access to your patient data. Scribedo administrators can access your account solely for support purposes with your explicit permission. Our AI processing systems analyze your recordings automatically without human review. We never share, sell, or use your patient data for purposes other than providing the agreed-upon services to you.</p>
          )
        },
        {
          question: "What is your data retention policy?",
          answer: (
            <div>
              <p>Our comprehensive data retention policy includes:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li>Active accounts: Data is retained as long as your account remains active</li>
                <li>Cancelled accounts: Data is retained for 30 days after cancellation</li>
                <li>Audio recordings: Can be automatically deleted after note generation if preferred</li>
                <li>Audit logs: Retained for 7 years to maintain compliance</li>
              </ul>
              <p className="mt-2">You can request data deletion at any time through your account settings or by contacting our support team.</p>
            </div>
          )
        },
        {
          question: "Do I need patient consent to use Scribedo?",
          answer: (
            <div>
              <p>While HIPAA permits the use of transcription services as part of normal healthcare operations, we recommend:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li>Informing patients that you use a secure, HIPAA-compliant transcription service</li>
                <li>Updating your Notice of Privacy Practices to mention the use of AI transcription</li>
                <li>For phone calls, utilizing our built-in consent notification</li>
              </ul>
              <p className="mt-2">Scribedo provides customizable consent templates that you can incorporate into your workflow.</p>
            </div>
          )
        }
      ]
    },
    {
      title: "Using Scribedo",
      items: [
        {
          question: "How accurate is Scribedo's transcription?",
          answer: (
            <div>
              <p>Scribedo achieves industry-leading transcription accuracy of 95-98% for most medical specialties. Factors that may affect accuracy include:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li>Audio quality and background noise levels</li>
                <li>Speaker clarity and accent characteristics</li>
                <li>Specialty-specific terminology</li>
                <li>Multiple speakers talking simultaneously</li>
              </ul>
              <p className="mt-2">Our AI continuously improves through machine learning, becoming more accurate over time as it processes more of your specific medical terminology and speaking patterns.</p>
            </div>
          )
        },
        {
          question: "How long does it take to process a recording?",
          answer: (
            <p>Processing time depends on the recording length, but most recordings are processed within minutes. A typical 15-minute patient visit is usually transcribed and structured into notes within 2-5 minutes. Longer recordings may require proportionally more time. You'll receive a notification when your notes are ready for review.</p>
          )
        },
        {
          question: "Can I edit the generated notes?",
          answer: (
            <p>Yes, all notes generated by Scribedo can be fully edited. Our comprehensive editor allows you to modify any aspect of the note, add or remove information, and adjust suggested medical codes. Changes are saved automatically, and you can access the revision history of each note.</p>
          )
        },
        {
          question: "How do I share notes with colleagues?",
          answer: (
            <div>
              <p>Scribedo offers several secure methods for sharing notes:</p>
              <ul className="list-disc ml-5 mt-2 space-y-2">
                <li>Direct sharing with other Scribedo users in your practice</li>
                <li>Export as PDF or Word documents for external sharing</li>
                <li>Secure email sharing with encryption</li>
              </ul>
              <p className="mt-2">All sharing methods maintain HIPAA compliance and include audit logging for security purposes.</p>
            </div>
          )
        }
      ]
    }
  ];

  // Filter FAQs based on search query
  const filterFAQs = (faqData: FAQCategoryProps[], query: string) => {
    if (!query) return faqData;

    const lowerCaseQuery = query.toLowerCase();
    
    return faqData
      .map((category) => {
        const filteredItems = category.items.filter(
          (item) =>
            item.question.toLowerCase().includes(lowerCaseQuery) ||
            (typeof item.answer === 'string' && item.answer.toLowerCase().includes(lowerCaseQuery))
        );
        
        return {
          ...category,
          items: filteredItems,
        };
      })
      .filter((category) => category.items.length > 0);
  };

  const filteredFAQs = filterFAQs(faqData, searchQuery);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>FAQ - Scribedo</title>
        <meta name="description" content="Frequently asked questions about Scribedo, the AI-powered medical transcription platform." />
      </Helmet>
      
      {/* Header with logo */}
      <header className="w-full bg-white shadow-sm py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center">
            <img src={logoImage} alt="Scribedo" className="h-10 w-auto" />
          </Link>
        </div>
      </header>
      
      <div className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about Scribedo and how it can help streamline your medical documentation workflow.
          </p>
        </div>

        {/* Search */}
        <div className="mb-10 max-w-md mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search FAQ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {filteredFAQs.length > 0 ? (
          filteredFAQs.map((category, index) => (
            <FAQCategory
              key={index}
              title={category.title}
              items={category.items}
            />
          ))
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-gray-600">No FAQs found matching your search.</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear search
            </button>
          </div>
        )}

        <div className="mt-16 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Still have questions?</h2>
          <p className="text-gray-600 mb-6">
            Our support team is here to help you with any questions you might have.
          </p>
          <a
            href="mailto:support@scribedo.com"
            className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-md"
          >
            Contact Support
          </a>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default FAQ; 